import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  Award,
  Eye,
  Clock,
  Building2,
  MapPin,
  ChevronRight,
  Filter,
  CheckCircle,
  XCircle,
  Hourglass,
  MessageSquare,
  Video,
  FileText
} from "lucide-react";

const MyApplications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("applications");
  const [statusFilter, setStatusFilter] = useState("all");

  // Get initial tab from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab && ["applications", "interviews", "offers", "profile_views"].includes(tab)) {
      setActiveTab(tab);
    }
    const status = params.get("status");
    if (status) {
      setStatusFilter(status);
    }
  }, [location.search]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch applications
      const appsRes = await fetch(`${API}/jobs/applications/mine`, {
        credentials: "include"
      });
      if (appsRes.ok) {
        const data = await appsRes.json();
        setApplications(data);
      }

      // Fetch interviews
      const intRes = await fetch(`${API}/calendar/interviews/mine`, {
        credentials: "include"
      });
      if (intRes.ok) {
        const data = await intRes.json();
        setInterviews(data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "applied":
      case "pending":
      case "under_review":
        return "bg-blue-500/20 text-blue-400";
      case "shortlisted":
      case "interview":
      case "interviewing":
        return "bg-yellow-500/20 text-yellow-400";
      case "offered":
      case "offer":
        return "bg-green-500/20 text-green-400";
      case "rejected":
      case "declined":
        return "bg-red-500/20 text-red-400";
      case "hired":
      case "accepted":
        return "bg-emerald-500/20 text-emerald-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "applied":
      case "pending":
      case "under_review":
        return <Hourglass className="w-4 h-4" />;
      case "shortlisted":
      case "interview":
      case "interviewing":
        return <Video className="w-4 h-4" />;
      case "offered":
      case "offer":
      case "hired":
      case "accepted":
        return <CheckCircle className="w-4 h-4" />;
      case "rejected":
      case "declined":
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  // Filter applications by status
  const filteredApplications = applications.filter(app => {
    if (statusFilter === "all") return true;
    return app.status?.toLowerCase() === statusFilter.toLowerCase();
  });

  // Get applications by status for stats
  const stats = {
    total: applications.length,
    under_review: applications.filter(a => ["applied", "pending", "under_review"].includes(a.status?.toLowerCase())).length,
    shortlisted: applications.filter(a => ["shortlisted", "interview", "interviewing"].includes(a.status?.toLowerCase())).length,
    offered: applications.filter(a => ["offered", "offer"].includes(a.status?.toLowerCase())).length,
    rejected: applications.filter(a => ["rejected", "declined"].includes(a.status?.toLowerCase())).length
  };

  const tabs = [
    { id: "applications", label: "Applications", count: applications.length, icon: Briefcase },
    { id: "interviews", label: "Interviews", count: interviews.length, icon: Calendar },
    { id: "offers", label: "Offers", count: stats.offered, icon: Award }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian" data-testid="my-applications-page">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-xl sm:text-2xl font-bold mb-1">
                MY <span className="text-crimson">APPLICATIONS</span>
              </h1>
              <p className="text-gray-400 text-sm sm:text-base">Track your job applications and interviews</p>
            </div>
            <Button
              onClick={() => navigate("/jobs")}
              className="bg-crimson hover:bg-crimson/90 text-sm sm:text-base w-full sm:w-auto"
            >
              Browse Jobs
            </Button>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <motion.div
              className={`bg-obsidian-light border border-[#222] p-3 sm:p-4 cursor-pointer hover:border-blue-500/30 transition-colors ${
                statusFilter === "all" ? "border-crimson" : ""
              }`}
              onClick={() => setStatusFilter("all")}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="text-xl sm:text-2xl font-bold text-white">{stats.total}</div>
              <div className="text-xs sm:text-sm text-gray-400">Total</div>
            </motion.div>
            <motion.div
              className={`bg-obsidian-light border border-[#222] p-3 sm:p-4 cursor-pointer hover:border-blue-500/30 transition-colors ${
                statusFilter === "under_review" ? "border-blue-500" : ""
              }`}
              onClick={() => setStatusFilter("under_review")}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="text-xl sm:text-2xl font-bold text-blue-400">{stats.under_review}</div>
              <div className="text-xs sm:text-sm text-gray-400">Review</div>
            </motion.div>
            <motion.div
              className={`bg-obsidian-light border border-[#222] p-3 sm:p-4 cursor-pointer hover:border-yellow-500/30 transition-colors ${
                statusFilter === "shortlisted" ? "border-yellow-500" : ""
              }`}
              onClick={() => setStatusFilter("shortlisted")}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-xl sm:text-2xl font-bold text-yellow-400">{stats.shortlisted}</div>
              <div className="text-xs sm:text-sm text-gray-400">Interview</div>
            </motion.div>
            <motion.div
              className={`bg-obsidian-light border border-[#222] p-3 sm:p-4 cursor-pointer hover:border-red-500/30 transition-colors ${
                statusFilter === "rejected" ? "border-red-500" : ""
              }`}
              onClick={() => setStatusFilter("rejected")}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-xl sm:text-2xl font-bold text-red-400">{stats.rejected}</div>
              <div className="text-xs sm:text-sm text-gray-400">Rejected</div>
            </motion.div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1.5 sm:gap-2 mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 whitespace-nowrap transition-colors text-sm sm:text-base ${
                  activeTab === tab.id
                    ? "bg-crimson text-white"
                    : "bg-obsidian-light text-gray-400 hover:text-white border border-[#333]"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                <span className="ml-1 px-2 py-0.5 bg-black/20 text-xs rounded-full">{tab.count}</span>
              </button>
            ))}
          </div>

          {/* Applications Tab */}
          {activeTab === "applications" && (
            <div className="space-y-4">
              {filteredApplications.length === 0 ? (
                <div className="bg-obsidian-light border border-[#222] p-12 text-center">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <h3 className="font-heading font-bold text-xl mb-2">No Applications Found</h3>
                  <p className="text-gray-400 mb-4">
                    {statusFilter !== "all" 
                      ? `No applications with status "${statusFilter}"`
                      : "You haven't applied to any jobs yet"}
                  </p>
                  <Button onClick={() => navigate("/jobs")} className="bg-crimson hover:bg-crimson/90">
                    Browse Jobs
                  </Button>
                </div>
              ) : (
                filteredApplications.map((app, i) => (
                  <motion.div
                    key={app.application_id || i}
                    className="bg-obsidian-light border border-[#222] p-4 hover:border-crimson/30 transition-colors"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading font-bold text-white">{app.job_title || "Job Position"}</h3>
                          <span className={`flex items-center gap-1 px-2 py-0.5 text-xs uppercase ${getStatusColor(app.status)}`}>
                            {getStatusIcon(app.status)}
                            {app.status || "Pending"}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {app.company || "Company"}
                          </span>
                          {app.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {app.location}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Applied {app.applied_at ? new Date(app.applied_at).toLocaleDateString() : "Recently"}
                          </span>
                        </div>
                        {app.match_score && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">Match Score: </span>
                            <span className={`text-sm font-bold ${
                              app.match_score >= 80 ? "text-green-400" :
                              app.match_score >= 60 ? "text-yellow-400" : "text-gray-400"
                            }`}>
                              {app.match_score}%
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {app.job_id && (
                          <Button
                            onClick={() => navigate(`/jobs/${app.job_id}`)}
                            variant="outline"
                            size="sm"
                            className="border-[#333] text-white"
                          >
                            View Job
                          </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* Interviews Tab */}
          {activeTab === "interviews" && (
            <div className="space-y-4">
              {interviews.length === 0 ? (
                <div className="bg-obsidian-light border border-[#222] p-12 text-center">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <h3 className="font-heading font-bold text-xl mb-2">No Interviews Scheduled</h3>
                  <p className="text-gray-400">You don't have any upcoming interviews</p>
                </div>
              ) : (
                interviews.map((interview, i) => (
                  <motion.div
                    key={interview.interview_id || i}
                    className="bg-obsidian-light border border-[#222] p-4 hover:border-crimson/30 transition-colors"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Video className="w-5 h-5 text-crimson" />
                          <h3 className="font-heading font-bold text-white">{interview.job_title || "Interview"}</h3>
                          <span className={`px-2 py-0.5 text-xs uppercase ${
                            interview.status === "completed" ? "bg-green-500/20 text-green-400" :
                            interview.status === "cancelled" ? "bg-red-500/20 text-red-400" :
                            "bg-blue-500/20 text-blue-400"
                          }`}>
                            {interview.status || "Scheduled"}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {interview.company || "Company"}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {interview.scheduled_at 
                              ? new Date(interview.scheduled_at).toLocaleString() 
                              : "TBD"}
                          </span>
                          {interview.interview_type && (
                            <span className="flex items-center gap-1 capitalize">
                              <Video className="w-4 h-4" />
                              {interview.interview_type}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {interview.meeting_link && interview.status !== "completed" && (
                          <Button
                            onClick={() => window.open(interview.meeting_link, "_blank")}
                            className="bg-crimson hover:bg-crimson/90"
                            size="sm"
                          >
                            Join Interview
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* Offers Tab */}
          {activeTab === "offers" && (
            <div className="space-y-4">
              {stats.offered === 0 ? (
                <div className="bg-obsidian-light border border-[#222] p-12 text-center">
                  <Award className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <h3 className="font-heading font-bold text-xl mb-2">No Offers Yet</h3>
                  <p className="text-gray-400">Keep applying! Your next opportunity is around the corner.</p>
                </div>
              ) : (
                applications
                  .filter(app => ["offered", "offer"].includes(app.status?.toLowerCase()))
                  .map((app, i) => (
                    <motion.div
                      key={app.application_id || i}
                      className="bg-obsidian-light border border-green-500/30 p-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Award className="w-5 h-5 text-green-400" />
                            <h3 className="font-heading font-bold text-white">{app.job_title || "Job Position"}</h3>
                            <span className="bg-green-500/20 text-green-400 px-2 py-0.5 text-xs uppercase">
                              Offer Received
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {app.company || "Company"}
                            </span>
                            {app.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {app.location}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                            Decline
                          </Button>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700">
                            Accept Offer
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MyApplications;
